// =============================================================================
// DashboardPage — v1.2
//
// Cambios respecto v1.1:
//   - Toggle "Ver todos los centros" movido a la cabecera superior (junto al
//     selector de periodo). Visible para GESTOR/ADMIN con ≥2 centros accesibles.
//   - Bloque "Comparativa entre centros" solo se renderiza si allSites=true
//     (opción A: ocultar bloque cuando toggle OFF).
//   - Los bloques MyDay y Business siguen respetando allSites (el backend
//     decide el scope de siteIds según el toggle).
//
// Principios (sin cambios):
//   - Estilo coherente con Sidebar y Ficha 360°: Material Symbols, slate/primary
//   - Sin spinners; placeholders animate-pulse
//   - Degradación elegante: EmptyBlock si un bloque viene null por error
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardSummary } from '../api/dashboard';
import type { DashboardSummary, DashboardPreset } from '../types/dashboard';

import { QuickActions } from '../components/dashboard/QuickActions';
import { PeriodSelector } from '../components/dashboard/PeriodSelector';
import { MyDayBlock } from '../components/dashboard/MyDayBlock';
import { BusinessBlock } from '../components/dashboard/BusinessBlock';
import { MultiSiteBlock } from '../components/dashboard/MultiSiteBlock';
import { EmptyBlock } from '../components/dashboard/EmptyBlock';

export function DashboardPage() {
  const { user, activeSite } = useAuth();

  // Estado de filtros
  const [preset, setPreset] = useState<DashboardPreset>('week');
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [allSites, setAllSites] = useState(false);

  // Estado de datos
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    // Guarda extra: si preset=custom sin fechas, no llamar al backend.
    if (preset === 'custom' && (!from || !to)) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await getDashboardSummary({
        preset,
        from: preset === 'custom' ? from ?? undefined : undefined,
        to: preset === 'custom' ? to ?? undefined : undefined,
        allSites,
      });
      setData(result);
      setLastRefreshed(new Date());
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error al cargar el dashboard';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [preset, from, to, allSites]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePeriodChange = (p: {
    preset: DashboardPreset;
    from: string | null;
    to: string | null;
  }) => {
    setPreset(p.preset);
    setFrom(p.from);
    setTo(p.to);
  };

  const formatTimeAgo = (d: Date): string =>
    d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

  // Reglas de visibilidad
  const accessibleCount = data?.scope.accessibleSiteIds.length ?? 0;
  const effectiveRole = data?.role ?? user?.role ?? 'USER';
  const shouldShowBusiness = effectiveRole !== 'USER';
  // v1.2: multiSite solo si toggle ON
  const shouldShowMultiSite =
    effectiveRole !== 'USER' && accessibleCount >= 2 && allSites;
  // Toggle visible si el usuario tiene ≥2 centros accesibles
  const canToggleAllSites =
    Boolean(data) && effectiveRole !== 'USER' && accessibleCount >= 2;

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      <header className="flex flex-col gap-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Hola, {user?.firstName}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {activeSite?.name ? (
                <>
                  Centro activo:{' '}
                  <span className="font-medium text-slate-700">
                    {activeSite.name}
                  </span>
                </>
              ) : (
                'Resumen de actividad'
              )}
            </p>
          </div>
          <QuickActions />
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3 bg-white rounded-2xl border border-slate-200 p-3">
          <div className="flex items-center gap-4 flex-wrap">
            <PeriodSelector
              preset={preset}
              from={from}
              to={to}
              onChange={handlePeriodChange}
              disabled={loading}
            />
            {canToggleAllSites && (
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none pl-3 border-l border-slate-200">
                <input
                  type="checkbox"
                  checked={allSites}
                  onChange={(e) => setAllSites(e.target.checked)}
                  disabled={loading}
                  className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                />
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-[18px] text-slate-400">
                    apartment
                  </span>
                  Ver todos los centros
                  <span className="text-xs text-slate-400">({accessibleCount})</span>
                </span>
              </label>
            )}
          </div>

          <div className="flex items-center gap-3 text-xs text-slate-400">
            {lastRefreshed && <span>Actualizado {formatTimeAgo(lastRefreshed)}</span>}
            <button
              type="button"
              onClick={fetchData}
              disabled={loading}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors disabled:opacity-50"
              title="Actualizar"
            >
              <span className="material-symbols-outlined text-lg">refresh</span>
            </button>
          </div>
        </div>
      </header>

      {/* Error general */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">
          <span className="material-symbols-outlined text-red-500">error</span>
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-700">
              No se pudo cargar el dashboard
            </p>
            <p className="text-xs text-red-600 mt-1">{error}</p>
          </div>
          <button
            type="button"
            onClick={fetchData}
            className="text-sm font-medium text-red-700 hover:text-red-900"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Bloque MyDay — siempre */}
      {loading || data?.myDay ? (
        <MyDayBlock data={data?.myDay ?? null} loading={loading} />
      ) : (
        <EmptyBlock title="Mi día" />
      )}

      {/* Bloque Business — si el rol no es USER */}
      {shouldShowBusiness &&
        (loading || data?.business ? (
          <BusinessBlock data={data?.business ?? null} loading={loading} />
        ) : (
          <EmptyBlock title="Negocio" />
        ))}

      {/* Bloque MultiSite — v1.2: solo si allSites=true */}
      {shouldShowMultiSite &&
        (loading || data?.multiSite ? (
          <MultiSiteBlock data={data?.multiSite ?? null} loading={loading} />
        ) : (
          <EmptyBlock title="Comparativa entre centros" />
        ))}

      {/* Footer informativo */}
      {data && !loading && (
        <footer className="text-xs text-slate-400 text-center pt-4">
          Periodo: {new Date(data.period.from).toLocaleDateString('es-ES')} →{' '}
          {new Date(data.period.to).toLocaleDateString('es-ES')} · Rol: {data.role}
          {allSites && accessibleCount >= 2 && (
            <> · {accessibleCount} centros incluidos</>
          )}
        </footer>
      )}
    </div>
  );
}

export default DashboardPage;
